import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { environment } from 'src/environments/environment';
import { Observable, throwError, of } from 'rxjs';
import { catchError, retry, delay, concatMap } from 'rxjs/operators';
import { AuthService } from '../auth/auth.service';

export interface CardPayment {
    id: number;
    stripePaymentId?: string;
    stripeSessionId?: string;
    amount: number;
    currency: string;
    status: 'pending' | 'completed' | 'failed';
    documentType: string;
    documentFormat: string;
    documentGenerated?: boolean;
    documentUrl?: string;
    createdAt: Date;
    updatedAt: Date;
    idUser: number;
    user?: {
        id: number;
        username?: string;
        email: string;
        firstName?: string;
        lastName?: string;
    };
}

export interface DocumentUrlResponse {
    documentUrl: string;
    documentType: string;
    documentFormat: string;
    status?: string;
    content?: string;
    contentType?: string;
    message?: string;
}

@Injectable({
    providedIn: 'root'
})
export class StripePaymentService {
    private stripe: Stripe | null = null;
    private authToken: string | null = null;

    setAuthToken(token: string) {
        this.authToken = token;
        localStorage.setItem('jwt_token', token);
    }

    private getAuthHeaders(): HttpHeaders {
        const token = localStorage.getItem('access_token');
        return new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token || ''}`
        });
    }

    constructor(private http: HttpClient, private authService: AuthService) {
        this.loadStripe();
        // Try to get auth token from localStorage when service is created
        this.authToken = localStorage.getItem('jwt_token');
    }

    private async loadStripe() {
        try {
            this.stripe = await loadStripe(environment.stripePublishableKey);
        } catch (error) {
            console.error('Failed to load Stripe:', error);
        }
    }

    private normalizeDocumentFormat(documentFormat: string): string {
        return documentFormat.toLowerCase();
    }

    private normalizeDocumentUniqueName(documentUniqueName: string): string {
        return documentUniqueName.toLowerCase().replace(/\s+/g, '_');
    }

    private normalizeDocumentUniqueGroup(documentUniqueGroup: string): string {
        return documentUniqueGroup.toLowerCase().replace(/\s+/g, '_');
    }

    getDocumentDetails(documentUniqueName: string): Observable<any> {
        if (!documentUniqueName) {
            console.error('Empty document unique name provided to getDocumentDetails');
            return throwError(() => new Error('Invalid document name'));
        }

        const headers = this.getAuthHeaders();

        const userString = localStorage.getItem('user');
        let idUser = null;
        let email = null;

        try {
            if (userString) {
                const user = JSON.parse(userString);
                idUser = user.id ? parseInt(user.id) : null;
                email = user.email || null;
            }
        } catch (error) {
            console.error('Error parsing user from localStorage:', error);
        }

        return this.http.post<CardPayment | null>(
            `${environment.backend}/api/cardpayments/document-details`,
            {
                documentUniqueName: documentUniqueName,
                idUser: idUser,
                email: email
            },
            { headers }
        ).pipe(
            retry(3)
        );
    }

    createCheckoutSession(
        documentUniqueName: string,
        isUser?: number,
        email?: string,
    ): Observable<{ sessionId: string, paymentId: number, userId?: number }> {
        const normalizedDocumentUniqueName = this.normalizeDocumentUniqueName(documentUniqueName)

        const headers = this.getAuthHeaders();

        return this.http.post<{ sessionId: string, paymentId: number, userId?: number }>(
            `${environment.backend}/api/cardpayments/create-checkout-session`,
            {
                documentUniqueName: normalizedDocumentUniqueName,
                idUser: isUser,
                email: email || '',
            },
            { headers }
        ).pipe(
            catchError(error => {
                console.error('Checkout session error:', error);
                return throwError(error);
            })
        );
    }

    async redirectToCheckout(sessionId: string): Promise<void> {
        console.log('Attempting to redirect to Stripe Checkout', { sessionId, stripe: !!this.stripe });

        if (!this.stripe) {
            console.error('Stripe is not loaded');
            throw new Error('Stripe is not loaded');
        }

        try {
            // Use Stripe's redirectToCheckout method to redirect to the actual Stripe Checkout page
            const result = await this.stripe.redirectToCheckout({ sessionId });

            console.log('Stripe redirectToCheckout result:', result);

            if (result.error) {
                console.error('Stripe checkout error:', result.error);
                throw result.error;
            }
        } catch (error) {
            console.error('Error in redirectToCheckout:', error);
            throw error;
        }
    }

    getDocumentUrlBySessionId(sessionId: string): Observable<DocumentUrlResponse> {
        console.log('Requesting document URL for session ID:', sessionId);

        // Use public endpoint that doesn't require auth
        return this.http.get<DocumentUrlResponse>(
            `${environment.backend}/api/cardpayments/public/session/${sessionId}/document`
        ).pipe(
            catchError(error => {
                console.error('Error getting document by public endpoint:', error);

                // Try alternative routes if the public one fails
                if (error.status === 404) {
                    console.log('Trying fallback routes for document URL');
                    return this.http.get<DocumentUrlResponse>(
                        `${environment.backend}/api/cardpayments/session-document/${sessionId}`
                    ).pipe(
                        catchError(fallbackError => {
                            console.error('Error with first fallback:', fallbackError);
                            return this.http.get<DocumentUrlResponse>(
                                `${environment.backend}/api/cardpayments/session/${sessionId}/document`
                            );
                        })
                    );
                }

                return throwError(error);
            })
        );
    }



















    // checkExistingDocument(
    //     documentUniqueGroup: string,
    //     documentUniqueName: string,
    //     documentFormat: string,
    //     documentType: string,
    //     documentPrice: number,
    //     documentCurrency: string
    // ): Observable<CardPayment | null> {
    //     // Normalize document type and format
    //     const normalizeDocumentUniqueName = this.normalizeDocumentUniqueName(documentUniqueName);
    //     const normalizedDocumentUniqueGroup = this.normalizeDocumentUniqueGroup(documentUniqueGroup);
    //     const normalizedFormat = this.normalizeDocumentFormat(documentFormat);

    //     // Always include headers, even if no token is present
    //     const headers = this.getAuthHeaders();

    //     // Get current user from local storage
    //     const userString = localStorage.getItem('user');
    //     let idUser = null;
    //     let email = null;

    //     try {
    //         if (userString) {
    //             const user = JSON.parse(userString);
    //             idUser = user.id ? parseInt(user.id) : null;
    //             email = user.email || null;
    //         }
    //     } catch (error) {
    //         console.error('Error parsing user from localStorage:', error);
    //     }
    //     // Verify we have valid data before making the request
    //     if (!normalizeDocumentUniqueName || !normalizedFormat) {
    //         console.error('Invalid document parameters:', { normalizeDocumentUniqueName, normalizedDocumentUniqueGroup, normalizedFormat });
    //         return throwError(() => new Error('Invalid document parameters'));
    //     }

    //     return this.http.post<CardPayment | null>(
    //         `${environment.backend}/api/cardpayments/check-existing-document`,
    //         {
    //             documentUniqueGroup: normalizedDocumentUniqueGroup,
    //             documentUniqueName: normalizeDocumentUniqueName,
    //             documentFormat: normalizedFormat,
    //             documentType: documentType,
    //             documentPrice: documentPrice,
    //             documentCurrency: documentCurrency,
    //             idUser: idUser,
    //             email: email
    //         },
    //         { headers }
    //     ).pipe(
    //         // Add basic retry (3 attempts total)
    //         retry(2),
    //         catchError(error => {
    //             console.error('Check existing document error:', error);

    //             let errorMessage = 'Failed to check existing document';

    //             // Handle different types of errors
    //             if (error.status === 0) {
    //                 console.error('Network error - server unreachable');
    //                 errorMessage = 'Server unreachable';
    //             } else if (error.status === 401) {
    //                 console.log('Authentication required for document check');
    //                 errorMessage = 'Authentication required';
    //             } else if (error.status === 404) {
    //                 console.log('No document found');
    //                 errorMessage = 'Document not found';
    //             } else if (error.status >= 500) {
    //                 console.error('Server error:', error.status);
    //                 errorMessage = 'Server error';
    //             }

    //             // Include more details in the error
    //             const enhancedError = new Error(errorMessage);
    //             (enhancedError as any).originalError = error;
    //             (enhancedError as any).documentInfo = {
    //                 documentUniqueGroup: normalizedDocumentUniqueGroup,
    //                 documentUniqueName: normalizeDocumentUniqueName,
    //                 documentFormat: normalizedFormat,
    //                 documentType: documentType,
    //                 documentPrice: documentPrice,
    //                 documentCurrency: documentCurrency
    //             };

    //             return throwError(() => enhancedError);
    //         })
    //     );
    // }

    // getUserPayments(): Observable<CardPayment[]> {
    //     return this.http.get<CardPayment[]>(`${environment.backend}/api/cardpayments`, {
    //         headers: this.getAuthHeaders()
    //     });
    // }

    // getPaymentById(documentId: string): Observable<any> {
    //     const headers = this.getAuthHeaders();

    //     return this.http.get(`${environment.backend}/api/cardpayments/payment/${documentId}`, {
    //         headers: headers
    //     }).pipe(
    //         catchError(error => {
    //             console.error('Error fetching payment details:', error);
    //             return throwError(() => new Error('Failed to retrieve payment details'));
    //         })
    //     );
    // }

    // getDocumentUrl(paymentId: number): Observable<DocumentUrlResponse> {
    //     return this.http.get<DocumentUrlResponse>(`${environment.backend}/api/cardpayments/${paymentId}/document`, {
    //         headers: this.getAuthHeaders()
    //     });
    // }

    // getPaymentBySessionId(sessionId: string): Observable<CardPayment> {
    //     console.log('Getting payment by session ID:', sessionId);

    //     // Use public endpoint that doesn't require auth
    //     return this.http.get<CardPayment>(
    //         `${environment.backend}/api/cardpayments/public/session/${sessionId}`
    //     ).pipe(
    //         catchError(error => {
    //             console.error('Error getting payment by public endpoint:', error);

    //             // Fallback to the original endpoint if the public one fails
    //             if (error.status === 404) {
    //                 console.log('Falling back to original endpoint for session data');
    //                 return this.http.get<CardPayment>(
    //                     `${environment.backend}/api/cardpayments/session/${sessionId}`,
    //                     { headers: this.getAuthHeaders() }
    //                 );
    //             }

    //             return throwError(error);
    //         })
    //     );
    // }

    

   
    // getDirectDownloadUrl(sessionId: string): Observable<{ downloadUrl: string, fileName?: string }> {
    //     const headers = this.getAuthHeaders();

    //     return this.http.get<{ downloadUrl: string, fileName?: string }>(
    //         `${environment.backend}/api/cardpayments/direct-download/${sessionId}`,
    //         { headers }
    //     ).pipe(
    //         catchError(error => {
    //             console.error('Error getting direct download URL', error);
    //             return throwError(() => new Error('Nu s-a putut obține linkul de descărcare'));
    //         })
    //     );
    // }

    // getPublicDownloadUrl(sessionId: string): Observable<{ downloadUrl: string, fileName?: string }> {
    //     const headers = this.getAuthHeaders();

    //     return this.http.get<{ downloadUrl: string, fileName?: string }>(
    //         `${environment.backend}/api/cardpayments/public-download/${sessionId}`,
    //         { headers }
    //     ).pipe(
    //         catchError(error => {
    //             console.error('Error getting public download URL', error);
    //             return throwError(() => new Error('Nu s-a putut obține linkul public de descărcare'));
    //         })
    //     );
    // }
} 